(() => {
  if (customElements.get('nut-routine-finder')) return;
  class NutRoutineFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.events = new AbortController();
      const options = {signal:this.events.signal};
      this.grid = this.querySelector('[data-routine-grid]');
      this.cards = [...this.querySelectorAll('[data-routine-card]')];
      this.controls = [...this.querySelectorAll('[data-filter]')];
      this.empty = this.querySelector('[data-empty]');
      this.count = this.querySelector('[data-count]');
      this.heading = this.querySelector('h1');
      this.description = this.querySelector('[data-description]');
      this.sort = this.querySelector('[data-sort]');
      this.category = this.querySelector('[data-category]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.allowed = {
        concern:['all',...this.controls.filter(e=>e.dataset.filter==='concern').map(e=>e.dataset.value)],
        skin:['all','oily','combination','normal','dry'],
        kind:['all','routine','product'],
        category:['all','cleansers','serums','moisturizers','sunscreen','oils','tools'],
        sort:['recommended','price-ascending','price-descending']
      };
      this.base = {concern:this.dataset.concern,skin:this.dataset.skin,kind:this.dataset.onlyRoutines==='true'?'routine':'all',sort:'recommended',category:'all'};
      this.controls.forEach(control => control.addEventListener('click',event => {
        if (event.button!==0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const key=control.dataset.filter, value=control.dataset.value;
        this.filters[key]=value;
        if(key==='skin' && value!=='all'){this.filters.kind='routine';this.filters.category='all';}
        if(key==='kind' && value!=='product') this.filters.category='all';
        if(key==='kind' && value==='product') this.filters.skin='all';
        this.apply(true);
      },options));
      this.querySelectorAll('[data-reset]').forEach(button => button.addEventListener('click',()=>{
        this.filters={...this.base,concern:'all',skin:'all'};
        this.apply(true);
      },options));
      this.category?.addEventListener('change',()=>{this.filters.category=this.category.value;if(this.filters.category!=='all'){this.filters.kind='product';this.filters.skin='all';}this.apply(true)},options);
      this.sort.addEventListener('change',()=>{this.filters.sort=this.sort.value;this.apply(true)},options);
      window.addEventListener('popstate',()=>{this.readURL();this.apply(false)},options);
      if ('IntersectionObserver' in window) {
        this.observer=new IntersectionObserver(entries=>entries.forEach(entry=>{
          if(!entry.isIntersecting || entry.target.hidden) return;
          this.reveal(entry.target);
        }),{threshold:.08});
        this.cards.forEach(card=>this.observer.observe(card));
      }
      this.readURL();
      this.dataset.enhanced='true';
      this.apply(false);
    }
    readURL() {
      const params=new URL(location.href).searchParams;
      this.filters={...this.base};
      for(const key of Object.keys(this.base)) {
        const value=params.get('nut_'+key);
        if(value && this.allowed[key].includes(value)) this.filters[key]=value;
      }
      if(this.dataset.onlyRoutines==='true') this.filters.kind='routine';
      if(this.filters.category!=='all' && this.category){this.filters.kind='product';this.filters.skin='all';}
      if(this.dataset.onlyRoutines==='true')this.filters.category='all';
      if(this.filters.skin!=='all')this.filters.kind='routine';
    }
    matches(card) {
      const f=this.filters;
      return (f.concern==='all' || card.dataset.concerns.split(' ').includes(f.concern)) &&
        (f.skin==='all' || card.dataset.skins.split(' ').includes(f.skin)) &&
        (f.kind==='all' || f.kind===card.dataset.kind) &&
        (f.category==='all' || (card.dataset.categories || '').split(' ').includes(f.category));
    }
    apply(updateURL) {
      this.animation?.cancel();
      const ordered=[...this.cards];
      if(this.filters.sort==='price-ascending') ordered.sort((a,b)=>Number(a.dataset.price)-Number(b.dataset.price));
      if(this.filters.sort==='price-descending') ordered.sort((a,b)=>Number(b.dataset.price)-Number(a.dataset.price));
      this.grid.append(...ordered);
      const visible=[];
      ordered.forEach(card=>{card.hidden=!this.matches(card);if(!card.hidden)visible.push(card)});
      this.controls.forEach(control=>{
        const selected=this.filters[control.dataset.filter]===control.dataset.value;
        if(selected)control.setAttribute('aria-current','true');else control.removeAttribute('aria-current');
      });
      this.sort.value=this.filters.sort;
      if(this.category)this.category.value=this.filters.category;
      this.empty.hidden=visible.length!==0;
      this.grid.hidden=visible.length===0;
      this.count.textContent=`${visible.length} ${this.dataset.resultLabel}`;
      const selected=this.controls.find(e=>e.dataset.filter==='concern' && e.dataset.value===this.filters.concern && e.dataset.value!=='all');
      this.heading.textContent=selected?.dataset.heading || this.dataset.title;
      this.description.textContent=selected?.dataset.description || this.dataset.description;
      this.querySelector('[data-reset]').hidden=this.filters.concern==='all' && this.filters.skin==='all' && this.filters.kind===this.base.kind && this.filters.category==='all' && this.filters.sort==='recommended';
      if(updateURL) {
        const url=new URL(location.href);
        for(const [key,value] of Object.entries(this.filters))url.searchParams.set('nut_'+key,value);
        url.searchParams.delete('page');
        history.pushState(null,'',url);
        if(!this.reduced.matches) this.animation=this.grid.animate([{opacity:.65},{opacity:1}],{duration:180,easing:'ease-out'});
      }
      visible.forEach(card=>{
        const rect=card.getBoundingClientRect();
        if(rect.bottom>0 && rect.top<innerHeight)this.reveal(card);
      });
    }
    reveal(card) {
      if(card.dataset.revealed) return;
      card.dataset.revealed='true';
      if(this.dataset.animate==='true' && !this.reduced.matches)
        card.animate([{opacity:0,transform:'translateY(12px)'},{opacity:1,transform:'translateY(0)'}],{duration:420,easing:'cubic-bezier(.22,1,.36,1)'});
    }
    disconnectedCallback() {
      this.events?.abort();this.observer?.disconnect();this.animation?.cancel();this.ready=false;
    }
  }
  customElements.define('nut-routine-finder',NutRoutineFinder);
})();
